import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { toast } from 'sonner';

/**
 * Global error handler that catches unhandled JavaScript errors,
 * including module import/syntax errors that bypass React error boundaries
 */
export function GlobalErrorHandler() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      const error = event.error || new Error(event.message);

      console.error('Global error caught:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
      });

      // Report to Sentry
      Sentry.withScope((scope) => {
        scope.setTag('errorType', 'unhandled');
        scope.setLevel('error');
        scope.setContext('errorEvent', {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        });
        Sentry.captureException(error);
      });

      // Show user-friendly error message
      const isImportError =
        event.message?.includes('import') ||
        event.message?.includes('export') ||
        event.message?.includes('module') ||
        event.message?.includes('Cannot resolve');

      if (isImportError) {
        toast.error('Module loading failed. Please refresh the page.', {
          description: 'There was an issue loading required application modules.',
          duration: 10000,
          action: {
            label: 'Reload',
            onClick: () => window.location.reload(),
          },
        });
      } else {
        toast.error('An unexpected error occurred', {
          description: 'The error has been reported. Please try refreshing the page.',
          duration: 8000,
          action: {
            label: 'Reload',
            onClick: () => window.location.reload(),
          },
        });
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);

      // Report to Sentry
      Sentry.withScope((scope) => {
        scope.setTag('errorType', 'unhandledRejection');
        scope.setLevel('error');
        Sentry.captureException(event.reason);
      });

      // Show user-friendly error message for async errors
      const reason = event.reason;
      const isNetworkError =
        reason?.name === 'NetworkError' ||
        reason?.code === 'NETWORK_ERROR' ||
        reason?.message?.includes('fetch');

      if (isNetworkError) {
        toast.error('Network error occurred', {
          description: 'Please check your connection and try again.',
          duration: 6000,
        });
      } else {
        toast.error('An unexpected error occurred', {
          description: 'Please try again or refresh the page.',
          duration: 6000,
        });
      }
    };

    // Add global error listeners
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return null;
}

export default GlobalErrorHandler;
